import {
  normalizePageTabValue,
  type PageTabValue,
} from '@constants/pageTabs';
import {
  getIframeQueryParamsFromSearch,
  type IframeQueryParams,
} from './queryParams';
import { isSupportedMarketCode } from '@constants/markets';
import {
  WIDGET_ERROR_MESSAGES,
  type WidgetErrorKey,
} from './widgetErrorMessages';

export type IframeParamIssueReason =
  | 'missing_ticker'
  | 'missing_mkt'
  | 'unsupported_mkt'
  | 'missing_token'
  | 'missing_tab'
  | 'unexpected_tab';

export interface IframeParamValidationIssue {
  param: WidgetErrorKey;
  reason: IframeParamIssueReason;
  message: string;
}

export interface IframeParamValidationResult {
  query: IframeQueryParams;
  tab?: PageTabValue;
  issues: IframeParamValidationIssue[];
  isValid: boolean;
}

export const validateIframeParams = (
  search: string,
): IframeParamValidationResult => {
  const query = getIframeQueryParamsFromSearch(search);
  const tab = normalizePageTabValue(query.tab);
  const issues: IframeParamValidationIssue[] = [];

  // Collect issues in the confirmed preflight priority order so callers that
  // only surface the first issue follow: token -> ticker -> mkt -> tab.
  if (!query.token) {
    issues.push({
      param: 'token',
      reason: 'missing_token',
      message: WIDGET_ERROR_MESSAGES.token,
    });
  }

  if (!query.ticker) {
    issues.push({
      param: 'ticker',
      reason: 'missing_ticker',
      message: WIDGET_ERROR_MESSAGES.ticker,
    });
  }

  if (!query.mkt) {
    issues.push({
      param: 'mkt',
      reason: 'missing_mkt',
      message: WIDGET_ERROR_MESSAGES.mkt,
    });
  } else if (!isSupportedMarketCode(query.mkt)) {
    // A non-empty but unsupported market (e.g. `CN`) is a frontend market
    // error that blocks page/API work; no ticker lookup is attempted.
    issues.push({
      param: 'mkt',
      reason: 'unsupported_mkt',
      message: WIDGET_ERROR_MESSAGES.mkt,
    });
  }

  if (!query.tab) {
    issues.push({
      param: 'tab',
      reason: 'missing_tab',
      message: WIDGET_ERROR_MESSAGES.tab,
    });
  } else if (!tab) {
    issues.push({
      param: 'tab',
      reason: 'unexpected_tab',
      message: WIDGET_ERROR_MESSAGES.tab,
    });
  }

  return {
    query,
    tab,
    issues,
    isValid: issues.length === 0,
  };
};
